import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import FocalImage from "../components/FocalImage";

const focal = (x) => ({ url: x.image, ...(x.image_focal || {}) });

const FILTERS = [
  { key: "all", en: "All", fr: "Tous" },
  { key: "talk", en: "Talks", fr: "Conférences" },
  { key: "dinner", en: "Dinners", fr: "Dîners" },
  { key: "workshop", en: "Workshops", fr: "Ateliers" },
  { key: "gala", en: "Galas", fr: "Galas" },
];
const DEFAULT_ORDER = ["hero", "featured", "grid"];

function fmtDate(d, lang) {
  try { return new Date(d).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { month: "long", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function Events() {
  const { content, lang, t } = useLang();
  const e = content.events || {};
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    api.get(`/events?type=${filter}`).then((r) => setEvents(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => {
    api.get("/page-config/events").then((r) => setLayout(r.data.sections))
      .catch(() => setLayout(DEFAULT_ORDER.map((id) => ({ id, enabled: true }))));
  }, []);

  const featured = events.find((ev) => ev.featured) || events[0];
  const rest = events.filter((ev) => ev.id !== featured?.id);

  const blocks = {
    hero: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-10">
        <Reveal><Overline>{e.hero_overline}</Overline></Reveal>
        <Reveal delay={0.1}><h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] mt-5 max-w-4xl text-primary" data-testid="events-title">{e.hero_title}</h1></Reveal>
        <Reveal delay={0.2}><p className="text-lg text-foreground/70 mt-6 max-w-2xl">{e.hero_body}</p></Reveal>
        <Reveal delay={0.3}>
          <div className="flex flex-wrap gap-3 mt-8">
            {FILTERS.map((f) => (
              <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "border border-primary/20 text-primary hover:bg-primary/5"}`}>
                {lang === "fr" ? f.fr : f.en}
              </button>
            ))}
          </div>
        </Reveal>
      </section>
    ),
    featured: featured ? (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-16">
        <Reveal>
          <Link to={`/events/${featured.id}`} data-testid="featured-event" className="grid lg:grid-cols-2 gap-8 items-center bg-card border border-primary/10 rounded-[2rem] overflow-hidden group">
            <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
              <div className="w-full h-full group-hover:scale-105 transition-transform duration-500"><FocalImage img={focal(featured)} alt="" /></div>
            </div>
            <div className="p-8 md:p-12">
              <span className="text-xs uppercase tracking-widest text-secondary font-semibold">{t("Next event", "Prochain événement")}</span>
              <h2 className="font-display text-3xl md:text-4xl font-medium text-primary mt-3">{lang === "fr" ? featured.title_fr : featured.title_en}</h2>
              <p className="text-foreground/70 mt-3">{lang === "fr" ? featured.excerpt_fr : featured.excerpt_en}</p>
              <div className="flex flex-col gap-2 mt-5 text-sm text-primary/80">
                <span className="flex items-center gap-2"><Calendar size={16} className="text-secondary" /> {fmtDate(featured.date, lang)} · {featured.time}</span>
                <span className="flex items-center gap-2"><MapPin size={16} className="text-secondary" /> {lang === "fr" ? featured.venue_fr : featured.venue_en}</span>
              </div>
              <span className="inline-flex items-center gap-2 mt-6 font-semibold text-primary group-hover:text-secondary transition-colors">{t("Get tickets", "Obtenir des billets")} <ArrowRight size={16} /></span>
            </div>
          </Link>
        </Reveal>
      </section>
    ) : null,
    grid: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((ev, i) => (
            <Reveal key={ev.id} delay={i * 0.06}>
              <Link to={`/events/${ev.id}`} data-testid={`event-card-${ev.id}`} className="block bg-card border border-primary/10 rounded-3xl overflow-hidden group h-full hover:-translate-y-1 transition-transform duration-300">
                <div className="aspect-[16/10] overflow-hidden relative">
                  <div className="w-full h-full group-hover:scale-105 transition-transform duration-500"><FocalImage img={focal(ev)} alt="" /></div>
                  {ev.members_only && <span className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full font-semibold">{t("Members only", "Membres")}</span>}
                </div>
                <div className="p-6">
                  <span className="text-xs uppercase tracking-widest text-secondary font-semibold">{ev.type}</span>
                  <h3 className="font-display text-2xl font-semibold text-primary mt-2">{lang === "fr" ? ev.title_fr : ev.title_en}</h3>
                  <div className="flex items-center gap-2 mt-3 text-sm text-primary/70"><Calendar size={14} className="text-secondary" /> {fmtDate(ev.date, lang)}</div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-semibold text-primary">{ev.price === 0 ? t("Free", "Gratuit") : `$${ev.price}`}</span>
                    <span className="text-sm text-secondary font-semibold">{t("Learn more", "En savoir plus")} →</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        {rest.length === 0 && (
          <p className="text-center text-muted-foreground py-10">{featured ? t("This is the only event in this category right now — check back soon!", "C'est le seul événement de cette catégorie pour l'instant — revenez bientôt !") : t("No events in this category yet.", "Aucun événement dans cette catégorie pour l'instant.")}</p>
        )}
      </section>
    ),
  };

  const order = layout || DEFAULT_ORDER.map((id) => ({ id, enabled: true }));

  return (
    <div data-testid="events-page">
      {order.filter((s) => s.enabled && blocks[s.id]).map((s) => (
        <React.Fragment key={s.id}>{blocks[s.id]}</React.Fragment>
      ))}
    </div>
  );
}
